/**
 * Subscription Product Search Module
 * Handles product search, session storage, and form field synchronization
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'subscriptionProducts';

  // Get Shopify routes root for locale-aware URLs (e.g., '/nl/' for Dutch)
  const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  
  let config = {
    discountPercentage: 20,
    currency: '€',
    moneyFormat: '{{amount_with_comma_separator}}',
    translations: {
      productAdded: 'Product added to subscription!'
    }
  };

// Shipping configuration
const SHIPPING_RATES = {
    netherlands: { rate: 4.95, freeThreshold: 30, currency: 'EUR' },
    belgium: { rate: 6.25, freeThreshold: 40, currency: 'EUR' },
    germany: { rate: 6.70, freeThreshold: 50, currency: 'EUR' },
    luxembourg: { rate: 10.95, freeThreshold: 100, currency: 'EUR' },
    france: { rate: 8.20, freeThreshold: 75, currency: 'EUR' },
    poland: { rate: 14.99, freeThreshold: 99, currency: 'PLN' }
  };

let selectedCountry = '';

  /**
   * Initialize the subscription search module
   */
  function init(options = {}) {
    config = { ...config, ...options };

    // Initially hide form section
    const formSection = document.querySelector('[data-form-section]');
    if (formSection) {
      formSection.style.display = 'none';
    }

    // Load saved products from session storage
    loadSavedProducts();

    // Setup event listeners
    setupSearchInput();
    setupEventDelegation();

    // Handle URL parameters
    handleUrlParameters();

    // Hide and setup form fields
    setupFormFields();

    // Trigger form initialization
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true
      }));
    }, 500);

  }

  /**
   * Load products from session storage and display them
   */
  function loadSavedProducts() {
    const products = getStoredProducts();
    if (products && products.length > 0) {
      renderSelectedProducts(products);
      updateTotals(products);
    }
  }

  /**
   * Get products from session storage
   */
  function getStoredProducts() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error reading from session storage:', e);
      return [];
    }
  }

  /**
   * Save products to session storage
   */
  function saveProducts(products) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(products));
      syncFormFields(products);
    } catch (e) {
      console.error('Error saving to session storage:', e);
    }
  }

  /**
   * Setup search input with debouncing
   */
  function setupSearchInput() {
    const searchInput = document.querySelector('[data-search-input]');
    if (!searchInput) return;

    let debounceTimer;
    searchInput.addEventListener('input', function(e) {
      const query = e.target.value.trim();
      
      clearTimeout(debounceTimer);
      
      if (query.length < 2) {
        clearSearchResults();
        return;
      }
      
      debounceTimer = setTimeout(() => {
        performSearch(query);
      }, 300);
    });
  }

  /**
   * Perform product search using Shopify's predictive search API
   */
  async function performSearch(query) {
    const resultsContainer = document.querySelector('[data-search-results]');
    if (!resultsContainer) return;

    // Show loading state
    const searchingText = config.translations.searching || 'Searching...';
    resultsContainer.innerHTML = `<div class="subscription-search-loading">${searchingText}</div>`;

    try {
      const response = await fetch(`${ROUTES_ROOT}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,variants.sku&resources[limit]=10`);
      const data = await response.json();
      
      if (data.resources && data.resources.results && data.resources.results.products) {
        renderSearchResults(data.resources.results.products);
      } else {
        showNoResults();
      }
    } catch (error) {
      console.error('Search error:', error);
      resultsContainer.innerHTML = '<div class="subscription-search-empty">Error performing search. Please try again.</div>';
    }
  }

  /**
   * Render search results
   */
  async function renderSearchResults(products) {
    const resultsContainer = document.querySelector('[data-search-results]');
    if (!resultsContainer) return;

    if (products.length === 0) {
      showNoResults();
      return;
    }

    // Show loading state while fetching full product data
    resultsContainer.innerHTML = '<div class="subscription-search-loading">Loading product details...</div>';

    // Always fetch full product data to get variants and SKUs
    // (Predictive search API returns empty variants array)
    const processedProducts = await Promise.all(products.map(async (product) => {
      try {
        const fullProductData = await fetchProductWithVariants(product.handle);
        if (fullProductData && fullProductData.variants) {
          return {
            ...product,
            fullVariants: fullProductData.variants,
            hasVariants: fullProductData.variants.length > 1 || product.price_min !== product.price_max
          };
        }
      } catch (error) {
        console.error(`Error fetching product data for ${product.handle}:`, error);
      }
      return product;
    }));

    const html = processedProducts.map(product => {
      // Use fullVariants if available (fetched from product API), otherwise fallback to variants from search
      const variants = product.fullVariants || product.variants || [];
      const firstVariant = variants[0];

      let price = firstVariant ? firstVariant.price : product.price;

      // Shopify product API returns price in cents, predictive search returns in euros
      // Check if price looks like cents (large number, no decimal) or euros (small number with decimal)
      const priceFloat = parseFloat(price);
      const isAlreadyInCents = priceFloat >= 100 || (typeof price === 'string' && !price.includes('.'));
      price = isAlreadyInCents ? Math.round(priceFloat) : Math.round(priceFloat * 100);

      // Get SKU - always try to get from first variant
      const sku = firstVariant?.sku || '';
      const variantId = firstVariant?.id || null;
      const image = product.image || (product.images && product.images[0]) || firstVariant?.featured_image;

      // Check if product has multiple variants
      const hasMultipleVariants = variants.length > 1;

      return `
        <div class="subscription-search-result-item" data-product-result>
          <div class="subscription-result-image" data-product-link data-url="${product.url}">
            ${image ? `<img src="${getResizedImage(image, 200)}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div class="subscription-result-info">
            <div class="subscription-result-title" data-product-link data-url="${product.url}">${product.title}</div>
            <div class="subscription-result-sku">${sku ? `SKU: ${sku}` : 'Loading SKU...'}</div>
            ${!hasMultipleVariants ? `<div class="subscription-result-price">${formatMoney(price)}</div>` : ''}
            ${hasMultipleVariants && variants.length > 0 ? `
              <div class="subscription-variants-list">
                ${variants.map(variant => {
                  // Check if price is already in cents (no decimal part or large number)
                  const priceFloat = parseFloat(variant.price);
                  const isAlreadyInCents = priceFloat >= 100 || variant.price.includes('.') === false;
                  const priceInCents = isAlreadyInCents ? Math.round(priceFloat) : Math.round(priceFloat * 100);

                  return `
                  <div class="subscription-variant-option" data-variant-option
                       data-variant-id="${variant.id}"
                       data-sku="${variant.sku || ''}"
                       data-title="${escapeHtml(product.title)}"
                       data-variant-title="${escapeHtml(variant.title)}"
                       data-price="${priceInCents}"
                       data-image="${variant.featured_image?.src || image || ''}"
                       data-product-handle="${product.handle}">
                    <div class="variant-info">
                      <span class="variant-title">${variant.title}</span>
                      ${variant.sku ? `<span class="variant-sku">SKU: ${variant.sku}</span>` : ''}
                    </div>
                    <span class="variant-price">${formatMoney(priceInCents)}</span>
                  </div>
                  `;
                }).join('')}
              </div>
            ` : ''}
          </div>
          ${!hasMultipleVariants ? `
            <button
              class="subscription-add-btn"
              data-add-product
              data-product-handle="${product.handle}"
              data-variant-id="${variantId}"
              data-sku="${sku}"
              data-title="${escapeHtml(product.title)}"
              data-price="${price}"
              data-price-min="${Math.round(parseFloat(product.price_min) * 100)}"
              data-price-max="${Math.round(parseFloat(product.price_max) * 100)}"
              data-has-variants="false"
              data-image="${image || ''}"
              data-url="${product.url}"
              aria-label="Add to subscription"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          ` : ''}
        </div>
      `;
    }).join('');

    resultsContainer.innerHTML = html;
  }

  /**
   * Show no results message
   */
  function showNoResults() {
    const resultsContainer = document.querySelector('[data-search-results]');
    if (resultsContainer) {
      const noResultsText = config.translations.noResults || 'No products found. Try a different search term.';
      resultsContainer.innerHTML = `<div class="subscription-search-empty">${noResultsText}</div>`;
    }
  }

  /**
   * Clear search results
   */
  function clearSearchResults() {
    const resultsContainer = document.querySelector('[data-search-results]');
    if (resultsContainer) {
      resultsContainer.innerHTML = '';
    }
  }

  /**
   * Setup event delegation for dynamic elements
   */
  function setupEventDelegation() {
    document.addEventListener('click', function(e) {
      // Product link (image or title) - redirect to PDP
      if (e.target.closest('[data-product-link]')) {
        const linkEl = e.target.closest('[data-product-link]');
        const url = linkEl.dataset.url;
        if (url) {
          window.location.href = url;
          return;
        }
      }

      // Add product button (for products without variants)
      if (e.target.closest('[data-add-product]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-add-product]');
        const productData = {
          variantId: btn.dataset.variantId,
          sku: btn.dataset.sku,
          title: btn.dataset.title,
          price: parseInt(btn.dataset.price),
          image: btn.dataset.image,
          url: btn.dataset.url,
          quantity: 1
        };
        addProduct(productData);
      }

      // Variant option selection
      if (e.target.closest('[data-variant-option]')) {
        e.preventDefault();
        const variantEl = e.target.closest('[data-variant-option]');
        const productData = {
          variantId: parseInt(variantEl.dataset.variantId),
          sku: variantEl.dataset.sku,
          title: variantEl.dataset.title + ' - ' + variantEl.dataset.variantTitle,
          price: parseInt(variantEl.dataset.price),
          image: variantEl.dataset.image,
          url: `${ROUTES_ROOT}products/${variantEl.dataset.productHandle}?variant=${variantEl.dataset.variantId}`,
          quantity: 1
        };
        addProduct(productData);
      }

      // Remove product button
      if (e.target.closest('[data-remove-product]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-remove-product]');
        const sku = btn.dataset.sku;
        removeProduct(sku);
      }

      // Quantity buttons
      if (e.target.matches('[data-qty-increase]')) {
        e.preventDefault();
        const sku = e.target.dataset.sku;
        updateQuantity(sku, 1);
      }

      if (e.target.matches('[data-qty-decrease]')) {
        e.preventDefault();
        const sku = e.target.dataset.sku;
        updateQuantity(sku, -1);
      }

      // Complete subscription button
      if (e.target.matches('[data-complete-subscription]')) {
        e.preventDefault();
        showFormSection();

        // Scroll to form section
        const formSection = document.querySelector('[data-form-section]');
        if (formSection) {
          setTimeout(() => {
            formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }

      // Back to search button
      if (e.target.matches('[data-back-to-search]')) {
        e.preventDefault();
        showSearchSection();
      }
    });

    // Country selection change
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-country-select]')) {
        selectedCountry = e.target.value;
        // Recalculate totals with new shipping
        const products = getStoredProducts();
        updateTotals(products);
      }
    });

    // Quantity input change
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-qty-input]')) {
        const sku = e.target.dataset.sku;
        const newQty = parseInt(e.target.value) || 1;
        setQuantity(sku, newQty);
      }
    });
  }

  /**
   * Add product to subscription list
   */
  async function addProduct(productData) {
    const products = getStoredProducts();

    // Only fetch full product details if we don't already have variant data
    // (i.e., when clicking the main "Add to Subscription" button for products with variants)
    if (productData.hasVariants === 'true' && !productData.variantId) {
      try {
        const fullProductData = await fetchProductWithVariants(productData.productHandle || productData.url.split('/products/')[1].split('?')[0]);
        if (fullProductData) {
          // For now, add the first/default variant
          const firstVariant = fullProductData.variants[0];
          const priceFloat = parseFloat(firstVariant.price);
          const isAlreadyInCents = priceFloat >= 100 || firstVariant.price.includes('.') === false;

          productData.variantId = firstVariant.id;
          productData.sku = firstVariant.sku;
          productData.price = isAlreadyInCents ? Math.round(priceFloat) : Math.round(priceFloat * 100);
          productData.image = firstVariant.featured_image?.src || fullProductData.featured_image?.src || productData.image;
          productData.title = fullProductData.title + ' - ' + firstVariant.title;
        }
      } catch (error) {
        console.error('Error fetching product variants:', error);
      }
    }

    // Check if product already exists by variant ID (more reliable than SKU)
    const existingIndex = products.findIndex(p =>
      p.variantId === productData.variantId ||
      (p.sku && p.sku === productData.sku && p.variantId === productData.variantId)
    );

    if (existingIndex >= 0) {
      // Increase quantity
      products[existingIndex].quantity += 1;
    } else {
      // Add new product
      products.push(productData);
    }

    saveProducts(products);
    renderSelectedProducts(products);
    updateTotals(products);

    // Show feedback
    showNotification(config.translations.productAdded);

    // On mobile, scroll to the selected products section
    if (window.innerWidth <= 768) {
      const selectedProductsSection = document.querySelector('.subscription-selected-products');
      if (selectedProductsSection) {
        setTimeout(() => {
          selectedProductsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    }
  }

  /**
   * Fetch product with full variant details
   */
  async function fetchProductWithVariants(productHandle) {
    try {
      const response = await fetch(`${ROUTES_ROOT}products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      return await response.json();
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  /**
   * Remove product from subscription list
   */
  function removeProduct(sku) {
    let products = getStoredProducts();
    products = products.filter(p => p.sku !== sku);
    
    saveProducts(products);
    renderSelectedProducts(products);
    updateTotals(products);
  }

  /**
   * Update product quantity
   */
  function updateQuantity(sku, delta) {
    let products = getStoredProducts();
    const product = products.find(p => p.sku === sku);
    
    if (product) {
      const newQuantity = product.quantity + delta;
      if (newQuantity <= 0) {
        // Remove product when quantity reaches 0
        products = products.filter(p => p.sku !== sku);
      } else {
        product.quantity = newQuantity;
      }
      saveProducts(products);
      renderSelectedProducts(products);
      updateTotals(products);
    }
  }

  /**
   * Set product quantity directly
   */
  function setQuantity(sku, quantity) {
    let products = getStoredProducts();
    const product = products.find(p => p.sku === sku);
    
    if (product) {
      if (quantity <= 0) {
        // Remove product when quantity is 0 or less
        products = products.filter(p => p.sku !== sku);
        saveProducts(products);
        renderSelectedProducts(products);
        updateTotals(products);
        return;
      }
      product.quantity = quantity;
      saveProducts(products);
      renderSelectedProducts(products);
      updateTotals(products);
    }
  }

  /**
   * Get resized image URL with width parameter
   */
  function getResizedImage(url, width = 200) {
    if (!url) return '';
    // Check if URL already has query parameters
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  /**
   * Render selected products list
   */
  function renderSelectedProducts(products) {
    const container = document.querySelector('[data-selected-products]');
    if (!container) return;

    if (products.length === 0) {
      container.innerHTML = `
        <div class="subscription-empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <p>No products selected yet. Search and add products to start your subscription.</p>
        </div>
      `;
      return;
    }

    const html = products.map(product => {
      // Show unit price, not total (quantity * price)
      const unitPrice = product.price;
      const discountedUnitPrice = unitPrice * (1 - config.discountPercentage / 100);

      return `
        <div class="subscription-selected-item">
          <div class="subscription-selected-image">
            ${product.image ? `<img src="${getResizedImage(product.image, 160)}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div class="subscription-selected-details">
            <div class="subscription-selected-title">${product.title}</div>
            <div class="subscription-selected-sku">SKU: ${product.sku}</div>
            <div class="subscription-selected-quantity">
              <button class="subscription-qty-btn" data-qty-decrease data-sku="${product.sku}">−</button>
              <input 
                type="number" 
                class="subscription-qty-input" 
                value="${product.quantity}" 
                min="0"
                data-qty-input
                data-sku="${product.sku}"
              >
              <button class="subscription-qty-btn" data-qty-increase data-sku="${product.sku}">+</button>
            </div>
            <div class="subscription-selected-price">
              <span class="original">${formatMoney(unitPrice)}</span>
              <span class="discounted">${formatMoney(discountedUnitPrice)}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = html;
  }

  /**
   * Calculate shipping cost based on selected country and subtotal (in cents)
   * Returns shipping cost in cents for EUR or raw value for PLN
   */
  function calculateShipping(subtotalInCents) {
    if (!selectedCountry || !SHIPPING_RATES[selectedCountry]) {
      return { cost: 0, costInCents: 0, isFree: false, noCountry: true };
    }

    const shippingConfig = SHIPPING_RATES[selectedCountry];
    const threshold = shippingConfig.freeThreshold;

    // Convert subtotal from cents to euros for comparison
    const subtotalInEuros = subtotalInCents / 100;

    // For PLN, convert EUR to PLN for threshold comparison
    let subtotalForComparison = subtotalInEuros;
    if (shippingConfig.currency === 'PLN') {
      subtotalForComparison = subtotalInEuros * 4.3; // Rough EUR to PLN rate
    }

    // Free shipping if above threshold
    if (subtotalForComparison >= threshold) {
      return { cost: 0, costInCents: 0, isFree: true, noCountry: false };
    }

    // Return shipping cost in cents for EUR, raw value for PLN
    const costInCents = shippingConfig.currency === 'EUR' ? Math.round(shippingConfig.rate * 100) : 0;
    return {
      cost: shippingConfig.rate,
      costInCents: costInCents,
      isFree: false,
      noCountry: false,
      currency: shippingConfig.currency
    };
  }

  /**
   * Update totals display
   */
  function updateTotals(products) {
    const totalsContainer = document.querySelector('[data-totals]');
    const completeSection = document.querySelector('[data-complete-section]');
    const completeBtn = document.querySelector('[data-complete-subscription]');
    const shippingEl = totalsContainer.querySelector('[data-shipping]');
    const finalTotalEl = totalsContainer.querySelector('[data-final-total]');

    if (products.length === 0) {
      totalsContainer.style.display = 'none';
      completeSection.style.display = 'none';
      return;
    }

    totalsContainer.style.display = 'block';
    completeSection.style.display = 'block';

    // Calculate totals
    const subtotal = products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const discountAmount = subtotal * (config.discountPercentage / 100);
    const discountedSubtotal = subtotal - discountAmount;
    const shipping = calculateShipping(discountedSubtotal);
    const total = discountedSubtotal + shipping.costInCents;

    // Update DOM
    const subtotalEl = totalsContainer.querySelector('[data-subtotal]');
    const discountEl = totalsContainer.querySelector('[data-discount]');
    const discountedSubtotalEl = totalsContainer.querySelector('[data-discounted-subtotal]');

    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    if (discountEl) discountEl.textContent = '-' + formatMoney(discountAmount);
    if (discountedSubtotalEl) discountedSubtotalEl.textContent = formatMoney(discountedSubtotal);
    if (shippingEl) {
      shippingEl.classList.remove('free', 'pending');
      if (shipping.noCountry) {
        shippingEl.textContent = config.translations.selectCountry || 'Select country';
        shippingEl.classList.add('pending');
      } else if (shipping.isFree) {
        shippingEl.textContent = config.translations.free || 'Free';
        shippingEl.classList.add('free');
      } else {
        if (shipping.currency === 'PLN') {
          shippingEl.textContent = shipping.cost.toFixed(2).replace('.', ',') + ' zł';
        } else {
          shippingEl.textContent = formatMoney(shipping.costInCents);
        }
      }
    }
    if (finalTotalEl) finalTotalEl.textContent = formatMoney(total);

    // Enable/disable subscribe button based on country selection
    if (completeBtn) {
      completeBtn.disabled = !selectedCountry;
    }

    // Update form fields
    syncFormFields(products, shipping, total);
  }

  /**
   * Helper to get form field from regular DOM or Shadow DOM
   */
  function getFormField(fieldId) {
    const fullId = `custom#${fieldId}`;

    // Try regular DOM first
    let field = document.getElementById(fullId);

    // If not found, try Shadow DOM
    if (!field) {
      const formsEmbed = document.querySelector('shopify-forms-embed');
      if (formsEmbed && formsEmbed.shadowRoot) {
        // Try getElementById equivalent in Shadow DOM
        field = formsEmbed.shadowRoot.getElementById(fullId);

        // If still not found, try querySelector with attribute selector
        if (!field) {
          field = formsEmbed.shadowRoot.querySelector(`[id="${fullId}"]`);
        }

      }
    }

    return field;
  }

  /**
   * Set value on a form field and trigger input event
   */
  function setFieldValue(field, value) {
    if (!field) return false;

    // Set the value using multiple methods
    field.value = value;
    field.setAttribute('value', value);

    // For React/Vue style forms, also try setting via property descriptor
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeInputValueSetter.call(field, value);

    // Trigger input and change events for form validation
    field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    field.dispatchEvent(new InputEvent('input', { bubbles: true, composed: true, data: value }));

    // Also set aria-invalid to false since we have a value now
    if (value) {
      field.setAttribute('aria-invalid', 'false');
    }

    return true;
  }

  /**
   * Sync form fields with current products
   */
  function syncFormFields(products, shipping = { cost: 0, costInCents: 0 }, finalTotal = 0) {
    // Prevent infinite sync loops
    if (isSyncing) return;
    isSyncing = true;

    try {
      // Calculate values - format: "SKU x quantity" for each product
      const skuList = products
        .filter(p => p.sku)
        .map(p => `${p.sku} x ${p.quantity}`)
        .join(', ');
      const totalValue = formatMoney(finalTotal);
      const countryValue = selectedCountry || '';

      let shippingValue = '';
      if (shipping.isFree) {
        shippingValue = config.translations.free || 'Free';
      } else if (!shipping.noCountry) {
        if (shipping.currency === 'PLN') {
          shippingValue = shipping.cost.toFixed(2).replace('.', ',') + ' zł';
        } else {
          shippingValue = formatMoney(shipping.costInCents);
        }
      }

      // Update SKUs field
      const skusField = getFormField('skus');
      if (skusField && skusField.value !== skuList) {
        setFieldValue(skusField, skuList);
      }

      // Update total price field
      const totalPriceField = getFormField('total_price');
      if (totalPriceField && totalPriceField.value !== totalValue) {
        setFieldValue(totalPriceField, totalValue);
      }

      // Update shipping country field
      const countryField = getFormField('shipping_country');
      if (countryField && countryField.value !== countryValue) {
        setFieldValue(countryField, countryValue);
      }

      // Update shipping cost field
      const shippingField = getFormField('shipping_cost');
      if (shippingField && shippingField.value !== shippingValue) {
        setFieldValue(shippingField, shippingValue);
      }

    // Also try direct approach - find all inputs and set by ID
      tryDirectFieldUpdate('custom#skus', skuList);
      tryDirectFieldUpdate('custom#total_price', totalValue);
      tryDirectFieldUpdate('custom#shipping_country', countryValue);
      tryDirectFieldUpdate('custom#shipping_cost', shippingValue);
    } finally {
      // Reset sync flag after a short delay
      setTimeout(() => {
        isSyncing = false;
      }, 100);
    }
  }

  /**
   * Try to update field directly by searching all possible locations
   */
  function tryDirectFieldUpdate(fieldId, value) {
    // Skip if already syncing
    if (isSyncing) return;

    // Search in Shadow DOM
    const formsEmbed = document.querySelector('shopify-forms-embed');
    if (formsEmbed && formsEmbed.shadowRoot) {
      const allInputs = formsEmbed.shadowRoot.querySelectorAll('input');
      allInputs.forEach(input => {
        if (input.id === fieldId && input.value !== value) {
          input.value = value;
          input.setAttribute('value', value);
          if (value) {
            input.setAttribute('aria-invalid', 'false');
          }
          // Dispatch events
          input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
          input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        }
      });
    }
  }

  /**
   * Setup and hide form fields
   */
  function setupFormFields() {
    // Helper function to hide field and all parent containers
    function hideFieldCompletely(fieldId, root = document) {
      const field = root.querySelector(`#custom\\#${fieldId}`) || root.querySelector(`[id="custom#${fieldId}"]`);
      if (field) {
        field.type = 'hidden';
        field.style.display = 'none';

        // Hide parent containers - try multiple levels
        let parent = field.parentElement;
        let levels = 0;
        while (parent && levels < 5) {
          // Check if this looks like a form field container
          if (parent.className && (
            parent.className.includes('formField') ||
            parent.className.includes('_formField') ||
            parent.className.includes('_formFieldContainer') ||
            parent.hasAttribute('data-testid')
          )) {
            parent.style.display = 'none';
            break;
          }
          // Also check for label
          const label = parent.querySelector(`label[for="custom#${fieldId}"], [id*="label-custom#${fieldId}"]`);
          if (label) {
            parent.style.display = 'none';
            break;
          }
          parent = parent.parentElement;
          levels++;
        }
      }
    }

    // Helper function to hide fields in Shadow DOM
    function hideFieldsInShadowDOM() {
      const formsEmbed = document.querySelector('shopify-forms-embed');
      if (formsEmbed && formsEmbed.shadowRoot) {
        const shadowRoot = formsEmbed.shadowRoot;

        // Hide fields inside Shadow DOM
        hideFieldCompletely('skus', shadowRoot);
        hideFieldCompletely('total_price', shadowRoot);
        hideFieldCompletely('shipping_country', shadowRoot);
        hideFieldCompletely('shipping_cost', shadowRoot);

      }
    }

    // Hide all custom form fields in regular DOM
    hideFieldCompletely('skus');
    hideFieldCompletely('total_price');
    hideFieldCompletely('shipping_country');
    hideFieldCompletely('shipping_cost');

    // Also try to hide in Shadow DOM
    hideFieldsInShadowDOM();

    // Retry after a delay in case Shadow DOM loads later
    setTimeout(hideFieldsInShadowDOM, 500);
    setTimeout(hideFieldsInShadowDOM, 1000);
    setTimeout(hideFieldsInShadowDOM, 2000);

    // Setup form submit interceptor
    setTimeout(setupFormSubmitInterceptor, 500);
    setTimeout(setupFormSubmitInterceptor, 1000);
    setTimeout(setupFormSubmitInterceptor, 2000);

    // Watch for Shadow DOM content changes
    observeShadowDOMChanges();

    // Initial sync
    const products = getStoredProducts();
    syncFormFields(products, { cost: 0, costInCents: 0, noCountry: true }, 0);
  }

  /**
   * Handle URL parameters for auto-adding products
   */
  function handleUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const skuParam = urlParams.get('sku');
    
    if (skuParam) {
      const skus = skuParam.split(',').map(s => s.trim()).filter(s => s);
      skus.forEach(sku => {
        fetchAndAddProductBySku(sku);
      });
    }
  }

  /**
   * Fetch product by SKU and add to subscription
   */
  async function fetchAndAddProductBySku(sku) {
    try {
      // First try to find by SKU in predictive search
      const response = await fetch(`${ROUTES_ROOT}search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,variants.sku&resources[limit]=20`);
      const data = await response.json();

      if (data.resources && data.resources.results && data.resources.results.products) {
        const products = data.resources.results.products;

        // Predictive search returns empty variants, so we need to fetch full product data
        for (const product of products) {
          const fullProductData = await fetchProductWithVariants(product.handle);
          if (fullProductData && fullProductData.variants) {
            const matchingVariant = fullProductData.variants.find(v => v.sku === sku);
            if (matchingVariant) {
              const priceFloat = parseFloat(matchingVariant.price);
              const isAlreadyInCents = priceFloat >= 100 || (typeof matchingVariant.price === 'string' && !matchingVariant.price.includes('.'));

              const productData = {
                variantId: matchingVariant.id,
                sku: matchingVariant.sku,
                title: fullProductData.title + (matchingVariant.title !== 'Default Title' ? ' - ' + matchingVariant.title : ''),
                price: isAlreadyInCents ? Math.round(priceFloat) : Math.round(priceFloat * 100),
                image: matchingVariant.featured_image?.src || fullProductData.featured_image || product.image,
                url: `${ROUTES_ROOT}products/${fullProductData.handle}?variant=${matchingVariant.id}`,
                quantity: 1
              };
              addProduct(productData);
              return;
            }
          }
        }
      }

      console.warn(`Product with SKU ${sku} not found`);
    } catch (error) {
      console.error(`Error fetching product with SKU ${sku}:`, error);
    }
  }

  /**
   * Global helper method to add products from anywhere
   * Usage: addToSubscription(variantId, quantity)
   * or: addToSubscription('20101', 2)
   */
  window.addToSubscription = async function(variantIdOrSku, quantity = 1) {
    try {
      // If it's a number or numeric string, treat as variant ID
      const isVariantId = !isNaN(variantIdOrSku);
      
      if (isVariantId) {
        // Fetch variant data
        const response = await fetch(`${ROUTES_ROOT}products/${variantIdOrSku}.js`);
        if (!response.ok) {
          // Try as SKU instead
          await fetchAndAddProductBySku(variantIdOrSku.toString());
          return;
        }
        
        const variant = await response.json();
        const priceFloat = parseFloat(variant.price);
        const isAlreadyInCents = priceFloat >= 100 || variant.price.includes('.') === false;

        const productData = {
          variantId: variant.id,
          sku: variant.sku,
          title: variant.product_title || variant.title,
          price: isAlreadyInCents ? Math.round(priceFloat) : Math.round(priceFloat * 100),
          image: variant.featured_image?.src || variant.image,
          url: `${ROUTES_ROOT}products/${variant.product_handle || variant.handle}?variant=${variant.id}`,
          quantity: quantity
        };
        addProduct(productData);
      } else {
        // Treat as SKU
        await fetchAndAddProductBySku(variantIdOrSku.toString());
      }
      
    } catch (error) {
      console.error('Error adding to subscription:', error);
    }
  };

  /**
   * Format money using Shopify's money format
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return config.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_space_separator}}', amount.replace('.', ' '));
  }

  /**
   * Format money with currency symbol
   */
  function formatMoneyWithCurrency(amount, currency = null) {
    const currencySymbol = currency || config.currency;
    if (currency === 'PLN') {
      // For PLN, amount is already in the correct currency units
      return amount.toFixed(2).replace('.', ',') + ' zł';
    }
    // For EUR, amount is in cents, so use formatMoney
    return currencySymbol + formatMoney(amount);
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Show form section and hide search section
   */
  function showFormSection() {
    const searchSection = document.querySelector('[data-search-section]');
    const formSection = document.querySelector('[data-form-section]');
    const selectedProducts = document.querySelector('.subscription-selected-products');

    if (searchSection && formSection) {
      searchSection.style.display = 'none';
      formSection.style.display = 'block';

      // Lock the selected products section (disable editing)
      if (selectedProducts) {
        selectedProducts.classList.add('is-locked');
      }

      // Trigger form initialization if needed
      setTimeout(() => {
        // Dispatch custom event for form apps to initialize
        window.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true
        }));

        // Also try to trigger Shopify Forms specifically
        if (window.ShopifyForms && window.ShopifyForms.init) {
          window.ShopifyForms.init();
        }

        // Re-apply form field hiding after form is visible
        setupFormFields();

        // Also sync the data one more time
        const products = getStoredProducts();
        const subtotal = products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
        const discountAmount = subtotal * (config.discountPercentage / 100);
        const discountedSubtotal = subtotal - discountAmount;
        const shipping = calculateShipping(discountedSubtotal);
        const total = discountedSubtotal + shipping.costInCents;
        syncFormFields(products, shipping, total);
      }, 200);

      // Setup form fields and sync data once after form loads
      const syncAndSetup = () => {
        setupFormFields();
        setupFormSubmitInterceptor();

        // Also sync the data
        const products = getStoredProducts();
        if (products.length > 0) {
          const subtotal = products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
          const discountAmount = subtotal * (config.discountPercentage / 100);
          const discountedSubtotal = subtotal - discountAmount;
          const shipping = calculateShipping(discountedSubtotal);
          const total = discountedSubtotal + shipping.costInCents;
          syncFormFields(products, shipping, total);
        }
      };

      // Single retry after form loads
      setTimeout(syncAndSetup, 500);

      // Observe for form success to clear subscription items
      setTimeout(observeFormSuccess, 500);
    }
  }

  /**
   * Show search section and hide form section
   */
  function showSearchSection() {
    const searchSection = document.querySelector('[data-search-section]');
    const formSection = document.querySelector('[data-form-section]');
    const selectedProducts = document.querySelector('.subscription-selected-products');

    if (searchSection && formSection) {
      formSection.style.display = 'none';
      searchSection.style.display = 'block';

      // Unlock the selected products section (re-enable editing)
      if (selectedProducts) {
        selectedProducts.classList.remove('is-locked');
      }
    }
  }

  /**
   * Observe Shadow DOM for form success banner
   */
  function observeFormSuccess() {
    const checkForSuccess = () => {
      const formsEmbed = document.querySelector('shopify-forms-embed');

      if (formsEmbed && formsEmbed.shadowRoot) {
        // Check immediately if success banner already exists
        const successBanner = formsEmbed.shadowRoot.querySelector('[data-testid="banner-success"]');
        if (successBanner) {
          handleFormSuccess();
          return;
        }

        // Also check for data-current-step="success" attribute
        const successStep = formsEmbed.shadowRoot.querySelector('[data-current-step="success"]');
        if (successStep) {
          handleFormSuccess();
          return;
        }

        // Set up observer if not already attached
        if (!formsEmbed._successObserverAttached) {
          formsEmbed._successObserverAttached = true;

          const observer = new MutationObserver(() => {
            const banner = formsEmbed.shadowRoot.querySelector('[data-testid="banner-success"]');
            const step = formsEmbed.shadowRoot.querySelector('[data-current-step="success"]');
            if (banner || step) {
              handleFormSuccess();
              observer.disconnect();
            }
          });

          observer.observe(formsEmbed.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-current-step']
          });
        }
      }
    };

    // Check immediately
    checkForSuccess();

    // Also check periodically for 5 seconds in case of timing issues
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      checkForSuccess();
      if (checkCount >= 5) {
        clearInterval(checkInterval);
      }
    }, 1000);
  }

  // Flag to prevent multiple success handling
  let formSuccessHandled = false;

  /**
   * Handle successful form submission
   */
  function handleFormSuccess() {
    // Prevent multiple executions
    if (formSuccessHandled) return;
    formSuccessHandled = true;

    // 1. Remove locked state from subscription items
    const selectedProducts = document.querySelector('.subscription-selected-products');
    if (selectedProducts) {
      selectedProducts.classList.remove('is-locked');
    }

    // 2. Hide subscribe button
    const completeSection = document.querySelector('[data-complete-section]');
    if (completeSection) {
      completeSection.style.display = 'none';
    }

    // 3. Clear session storage (will take effect on page refresh)
    sessionStorage.removeItem('subscriptionProducts');

  }

  // Flag to prevent infinite sync loops
  let isSyncing = false;
  let syncTimeout = null;

  /**
   * Observe Shadow DOM changes to catch when form fields become available
   */
  function observeShadowDOMChanges() {
    const formsEmbed = document.querySelector('shopify-forms-embed');

    if (formsEmbed && formsEmbed.shadowRoot && !formsEmbed._observerAttached) {
      formsEmbed._observerAttached = true;

      // Create a mutation observer to watch for form changes
      const observer = new MutationObserver((mutations) => {
        // Skip if we're currently syncing (to prevent infinite loops)
        if (isSyncing) return;

        // Only trigger on childList changes (new elements added), not attribute changes
        const hasNewNodes = mutations.some(m => m.type === 'childList' && m.addedNodes.length > 0);
        if (!hasNewNodes) return;

        // Debounce sync calls
        if (syncTimeout) clearTimeout(syncTimeout);
        syncTimeout = setTimeout(() => {
          const products = getStoredProducts();
          if (products.length > 0) {
            const subtotal = products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
            const discountAmount = subtotal * (config.discountPercentage / 100);
            const discountedSubtotal = subtotal - discountAmount;
            const shipping = calculateShipping(discountedSubtotal);
            const total = discountedSubtotal + shipping.costInCents;
            syncFormFields(products, shipping, total);
          }
        }, 500);
      });

      observer.observe(formsEmbed.shadowRoot, {
        childList: true,
        subtree: true,
        attributes: false // Don't watch attribute changes to prevent loops
      });

    }
  }

  /**
   * Setup form submit interceptor to populate fields before submission
   */
  function setupFormSubmitInterceptor() {
    // Try to find the form in Shadow DOM
    const formsEmbed = document.querySelector('shopify-forms-embed');
    
    if (formsEmbed && formsEmbed.shadowRoot) {
      const form = formsEmbed.shadowRoot.querySelector('form');
      if (form) {
        // Remove any existing listener to avoid duplicates
        form.removeEventListener('submit', handleFormSubmit);
        form.addEventListener('submit', handleFormSubmit, true);
      }
    }

    // Also try regular DOM
    const regularForm = document.querySelector('.subscription-form-container form');
    if (regularForm) {
      regularForm.removeEventListener('submit', handleFormSubmit);
      regularForm.addEventListener('submit', handleFormSubmit, true);
    }
  }

  /**
   * Handle form submission - populate fields before submit
   */
  function handleFormSubmit(e) {

    // Get current products and calculate totals
    const products = getStoredProducts();
    const subtotal = products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const discountAmount = subtotal * (config.discountPercentage / 100);
    const discountedSubtotal = subtotal - discountAmount;
    const shipping = calculateShipping(discountedSubtotal);
    const total = discountedSubtotal + shipping.costInCents;

    // Sync fields one more time before submit
    syncFormFields(products, shipping, total);

  }

  /**
   * Show notification message (toast style)
   */
  function showNotification(message) {
    // Create toast element if it doesn't exist
    let toast = document.querySelector('.subscription-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'subscription-toast';
      toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #333;
        color: #fff;
        padding: 12px 24px;
        border-radius: 4px;
        z-index: 9999;
        opacity: 0;
        transition: opacity 0.3s ease;
        font-size: 14px;
      `;
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.style.opacity = '1';

    setTimeout(() => {
      toast.style.opacity = '0';
    }, 2500);
  }

  // Export public API
  window.SubscriptionSearch = {
    init: init,
    addProduct: addProduct,
    removeProduct: removeProduct,
    getProducts: getStoredProducts,
    clearProducts: function() {
      sessionStorage.removeItem(STORAGE_KEY);
      loadSavedProducts();
    }
  };

})();

